import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { User, Session } from '@supabase/supabase-js';
import { localDatabase } from '@/services/localDatabase';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      if (session?.user?.id) runOneShotDedupCleanup(session.user.id);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      setIsLoading(false);
      if (session?.user?.id) runOneShotDedupCleanup(session.user.id);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, isLoading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

/**
 * One-shot recovery: after the dedup-merge logic was introduced, some users
 * still have orphan local IndexedDB rows (different ids than server) that
 * inflate totals. We run a single hard sync per user — pull all server ids,
 * drop any non-dirty local rows whose id isn't on the server. Guarded by a
 * localStorage flag so it never runs twice.
 */
const DEDUP_FLAG_KEY = 'wren:dedup-cleanup-v2';
async function runOneShotDedupCleanup(userId: string) {
  try {
    const flagKey = `${DEDUP_FLAG_KEY}:${userId}`;
    if (localStorage.getItem(flagKey)) return;
    if (!navigator.onLine) return;

    // Pull every server expense + income id for this user (id only — small payload).
    const [{ data: expRows }, { data: incRows }] = await Promise.all([
      supabase.from('expenses').select('id, user_id, amount, currency, merchant, category, note, expense_date, tag, synced, created_at, updated_at').eq('user_id', userId),
      supabase.from('income').select('id, user_id, amount, currency, source, category, income_date, is_recurring, recurrence_interval, note, created_at, updated_at').eq('user_id', userId),
    ]);

    if (!expRows || !incRows) return; // bail without setting flag — retry next session

    const expReconciled = await localDatabase.reconcileExpensesWithServer(userId, expRows as any);
    const incReconciled = await localDatabase.reconcileIncomeWithServer(userId, incRows as any);
    const expRemoved = await localDatabase.hardSyncExpenses(userId, expRows.map(r => r.id));
    const incRemoved = await localDatabase.hardSyncIncome(userId, incRows.map(r => r.id));

    localStorage.setItem(flagKey, new Date().toISOString());
    if (expRemoved + incRemoved + expReconciled + incReconciled > 0) {
      console.info(`[dedup-cleanup] reconciled ${expReconciled} expenses, ${incReconciled} income; removed ${expRemoved} expenses, ${incRemoved} income orphans`);
      window.dispatchEvent(new CustomEvent('wren:data-reconciled'));
    }
  } catch (err) {
    console.warn('[dedup-cleanup] failed', err);
  }
}
